import React, { useState, useEffect, useRef, useCallback } from 'react';
import './inputSelect.scss';
import { batchColorFormatTag, formatTag } from '../../utils/constant';
import { Tooltip } from 'react-tooltip';

const InputSelect = ({
	label,
	onFocusCb = () => {},
	onBlurCb = () => {},
	onKeyDownCb = null,
	onSelect,
	options = [],
	placeholder,
	onAddNew,
	onChange,
	returnInputValue,
	required,
	value,
	disabled,
	autoFocus,
	infoIcon,
	name,
	propInputRef,
	onClick,
	error,
	isLocalValidationError,
	onfucusCapture,
	className,
	moreInfobtn,
	tolipIcon = null,
	onClickDropdownIcon = () => null,
	leftIcons = [],
}) => {
	const [inputValue, setInputValue] = useState('');
	const [isDropdownVisible, setIsDropdownVisible] = useState(false);
	const [filteredOptions, setFilteredOptions] = useState(options || []);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const dropdownRef = useRef(null);
	const selectedItemRef = useRef(null);
	const inputRef = useRef(null);
	const handleDropDown = () => {
		setIsDropdownVisible(true);
	};

	useEffect(() => {
		function changeInputValue() {
			setInputValue(value ?? '');
		}
		changeInputValue();
	}, [value]);

	function getMonthAndYear(dateStr) {
		const date = new Date(dateStr);
		const year = date.getFullYear() % 100;
		const month = date.getMonth() + 1;
		return `${month}/${year}`;
	}

	useEffect(() => {
		if (!returnInputValue || !value) {
			const foundOption = options?.find((option) => option?.key === value);
			if (foundOption) {
				setInputValue(foundOption.value);
			}
		}
	}, [returnInputValue, value, options]);

	const handleFilterInputValues = useCallback(() => {
		const uniqueOptions = options.reduce((acc, current) => {
			if (!acc.find((item) => item.key === current.key)) {
				acc.push(current);
			}
			return acc;
		}, []);

		const filtered = inputValue
			? uniqueOptions.filter(
					(option) =>
						option?.value?.toString().toLowerCase().includes(inputValue.toString().toLowerCase()) ||
						(option?.short_name !== undefined &&
							option?.short_name
								?.toString()
								.toLowerCase()
								.includes(inputValue.toString().toLowerCase())),
				)
			: uniqueOptions;

		setFilteredOptions((prev) =>
			JSON.stringify(prev) === JSON.stringify(filtered) ? prev : filtered,
		);
		setSelectedIndex(-1);
	}, [inputValue, options]);

	useEffect(() => {
		handleFilterInputValues();
	}, [inputValue, options]);

	useEffect(() => {
		const onReady = () => {
			if (autoFocus && inputRef.current) {
				inputRef.current.focus();
				handleDropDown();
			}
		};

		if (document.readyState === 'complete') {
			onReady();
		} else {
			window.addEventListener('load', onReady);
			return () => window.removeEventListener('load', onReady);
		}
	}, [autoFocus]);

	useEffect(() => {
		if (selectedItemRef.current && dropdownRef.current) {
			const dropdown = dropdownRef.current;
			const selectedItem = selectedItemRef.current;
			const dropdownRect = dropdown.getBoundingClientRect();
			const selectedItemRect = selectedItem.getBoundingClientRect();

			if (selectedItemRect.bottom > dropdownRect.bottom) {
				dropdown.scrollTop += selectedItemRect.bottom - dropdownRect.bottom;
			} else if (selectedItemRect.top < dropdownRect.top) {
				dropdown.scrollTop -= dropdownRect.top - selectedItemRect.top;
			}
		}
	}, [selectedIndex]);

	const handleInputChange = (e) => {
		setInputValue(e.target.value);
		setIsDropdownVisible(true);
		if (onChange) {
			onChange(e.target.value);
			error = null;
		}
	};

	const handleOptionClick = (option) => {
		setInputValue(option && option?.value ? option?.value : '');
		setIsDropdownVisible(false);
		if (onSelect && option !== undefined) {
			onSelect(option);
		} else {
			onChange(
				returnInputValue
					? option && option.value
						? option.value
						: ''
					: option && option.key
						? option.key
						: '',
			);
		}
	};

	const handleBlur = () => {
		setTimeout(() => {
			onBlurCb();
			setIsDropdownVisible(false);
		}, 200);
	};

	const handleKeyDown = (e) => {
		if (isDropdownVisible) {
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setSelectedIndex((prevIndex) => (prevIndex < filteredOptions.length ? prevIndex + 1 : 0));
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : filteredOptions.length));
			} else if (e.key === 'Enter') {
				e.preventDefault();
				if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
					handleOptionClick(filteredOptions[selectedIndex]);
				} else if (selectedIndex === filteredOptions.length) {
					handleAddNew();
				}
			} else if (e.key === 'Tab') {
				if (filteredOptions[selectedIndex]) {
					handleOptionClick(filteredOptions[selectedIndex]);
				} else {
					var options = filteredOptions.filter((item) => item.value === inputValue);
					if (options.length > 1) {
						options = options.filter((item) => item.key === value);
					}
					if (options.length === 1) {
						handleOptionClick(options[0]);
					} else {
						handleOptionClick();
					}
				}
			}
		}
		if (e.key === 'Tab' && e.shiftKey && onKeyDownCb !== null) {
			onKeyDownCb(e);
		}
	};

	const handleAddNew = () => {
		if (onAddNew) {
			onAddNew(inputValue);
		}
		setIsDropdownVisible(false);
	};

	useEffect(() => {
		return () => {
			setIsDropdownVisible(false);
		};
	}, []);

	return (
		<div
			className={`input-select ${className}`}
			style={{ position: 'relative', display: 'inline-block', width: '100%' }}
		>
			<div
				className="mb-2"
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-start',
				}}
			>
				{label && <label className="form-label mb-0">{label}</label>}
				{required && <span style={{ color: 'red', marginLeft: '.2rem' }}>*</span>}

				{tolipIcon && (
					<div className="px-2">
						<img
							src={tolipIcon.icon}
							alt="info"
							width={15}
							height={15}
							data-tooltip-id={`tooltip-${tolipIcon.name}`}
							data-tooltip-content={tolipIcon.title}
							className="cursor-pointer"
						/>

						<Tooltip id={`tooltip-${name}`} />
					</div>
				)}
				{moreInfobtn && (
					<span style={{ marginLeft: '140px', fontWeight: '600', fontSize: '14px' }}>
						{moreInfobtn}
					</span>
				)}
			</div>

			<div
				style={{
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					border: error || isLocalValidationError ? '1px solid #f71326' : '1px solid #ccc',
					borderRadius: '6px',
				}}
				className="mb-3"
			>
				{/* Left side icons (Images) */}

				{leftIcons.length > 0 && (
					<div
						className="left-icons"
						style={{
							position: 'absolute',
							right: '35px',
							display: 'flex',
							zIndex: '1',
							pointerEvents: 'auto',
						}}
					>
						{leftIcons.map((icon, index) => (
							<img
								key={index}
								src={icon.src}
								alt={icon.alt}
								style={{
									width: '18px',
									height: '18px',
									marginRight: index === leftIcons.length - 1 ? '0' : '8px',
									cursor: 'pointer',
									zIndex: '1',
								}}
								onClick={icon.onClick}
							/>
						))}
					</div>
				)}

				{/* Input Field */}
				<input
					ref={propInputRef ? propInputRef : inputRef}
					type="text"
					value={inputValue}
					onChange={handleInputChange}
					onBlur={() => {
						handleBlur();
					}}
					onFocus={() => {
						onFocusCb();
						setIsDropdownVisible(true);
					}}
					onKeyDown={(e) => handleKeyDown(e)}
					placeholder={placeholder}
					style={{
						width: '100%',
						padding: '8px',
						boxSizing: 'border-box',
						borderRadius: '6px',
						paddingRight: '30px',
						background: '#FCFCFC',
						border: '0px solid #ccc',
						height: '34px',
					}}
					disabled={disabled}
					autoFocus={autoFocus}
					name={name}
					className={`inputBG`}
					onClick={handleDropDown}
					onFocusCapture={onfucusCapture}
				/>
				<i
					className="fa fa-caret-down"
					style={{
						position: 'absolute',
						top: '50%',
						right: '10px',
						transform: 'translateY(-50%)',
						cursor: 'pointer',
					}}
					onClick={() => {
						setIsDropdownVisible((prevState) => !prevState);
						if (inputRef.current) {
							inputRef.current.focus();
						}
					}}
				/>
			</div>

			{error && (
				<div
					className="error-text"
					style={{ color: 'red', fontSize: '12px', marginBottom: '10px' }}
				>
					{error}
				</div>
			)}
			{isDropdownVisible && !disabled && (
				<span
					className="d-block w-100"
					style={{ height: '150px', position: 'absolute' }}
				>
					<ul
						className="dropdown-list"
						ref={dropdownRef}
						style={{
							position: 'absolute',
							top: '0',
							left: '0',
							right: '0',
							border: '1px solid #ccc',
							borderRadius: '8px',
							maxHeight: '115px',
							minHeight: '115px',
							overflowY: 'auto',
							backgroundColor: '#FCFCFC',
							listStyleType: 'none',
							padding: '0',
							margin: '0',
							width: '100%',
							boxSizing: 'border-box',
							alignContent: 'flex-start',
							zIndex: 1000,
							borderBottomRightRadius: onAddNew ? '0' : '',
							borderBottomLeftRadius: onAddNew ? '0' : '',
						}}
					>
						{filteredOptions.length > 0 ? (
							filteredOptions.map((option, index) => (
								<li
									key={index}
									ref={index === selectedIndex ? selectedItemRef : null}
									onClick={() => handleOptionClick(option)}
									onMouseEnter={() => setSelectedIndex(index)}
									style={{
										padding: '8px',
										cursor: 'pointer',
										textAlign: 'left',
										backgroundColor: selectedIndex === index ? '#bde4ff' : 'transparent',
									}}
									onMouseDown={(e) => e.preventDefault()}
								>
									{option?.type && <div className="fs-4 fw-semibold text-dark">{option?.type}</div>}
									{option.tag ? (
										<div className="d-flex justify-content-between ">
											<span style={{ fontSize: '12px' }}>{option.value}</span>
											<span style={{ color: batchColorFormatTag(option.tag), fontSize: '12px' }}>
												{formatTag(option.tag)}
											</span>
										</div>
									) : (
										option?.month || option.value
									)}

									{option?.qty || option?.expiry_date || option?.mrp ? (
										<div
											style={{
												display: 'flex',
												flexDirection: 'row',
												justifyContent: 'space-between',
												padding: '5px, 2px',
												fontSize: '10px',
												fontWeight: '400',
											}}
										>
											<span>{`QTY: ${option?.qty}`}</span>
											<span>{`EXP: ${option?.expiry_date}`}</span>
											<span>{`MRP: ₹ ${option?.mrp}`}</span>
										</div>
									) : (
										''
									)}
								</li>
							))
						) : (
							<li
								style={{
									padding: '8px',
									cursor: 'default',
									textAlign: 'start',
								}}
							>
								No options found
							</li>
						)}
					</ul>
					<span
						className="addNew"
						style={{
							backgroundColor: selectedIndex === filteredOptions.length ? '#bde4ff' : '#ffff',
							borderBottom: onAddNew ? '1px solid rgb(204, 204, 204)' : '0',
						}}
					>
						{onAddNew && (
							<li
								ref={selectedIndex === filteredOptions.length ? selectedItemRef : null}
								onClick={handleAddNew}
								style={{
									padding: '8px',
									cursor: 'pointer',
									textAlign: 'start',
									color: 'blue',
									listStyle: 'none',
								}}
								onMouseDown={(e) => e.preventDefault()}
							>
								Add New
							</li>
						)}
					</span>
				</span>
			)}
		</div>
	);
};

export default InputSelect;
